import { Link } from "react-router-dom";
import { ShieldCheck, Smartphone, RotateCcw, MessageCircle, Lock, HelpCircle, ChevronDown } from "lucide-react";

const METHODS = [
  { name: "Wave", bg: "#1DC4FF", fg: "#fff", initials: "W", desc: "Ouvrez l'application Wave, scannez ou validez la demande de paiement recue, confirmez avec votre code secret. Frais reduits, confirmation instantanee." },
  { name: "Orange Money", bg: "#FF6600", fg: "#fff", initials: "OM", desc: "Validez le paiement depuis le pop-up USSD (#144#) ou l'application Max it. Confirmez avec votre code secret Orange Money." },
  { name: "MTN MoMo", bg: "#FFCC00", fg: "#1a1a1a", initials: "MTN", desc: "Une demande de paiement arrive sur votre telephone (*133#). Validez avec votre code MoMo pour confirmer la transaction." },
  { name: "Moov Money", bg: "#0060AE", fg: "#fff", initials: "MV", desc: "Confirmez la demande de paiement Moov Money (*155#) avec votre code secret. Le recu SMS fait office de preuve." },
  { name: "CinetPay", bg: "#0e9f6e", fg: "#fff", initials: "CP", desc: "Notre agregateur de paiement securise : il traite tous les moyens ci-dessus via une page de paiement chiffree (HTTPS)." },
];

const FAQ = [
  { q: "Quels sont les moyens de paiement acceptes ?", a: "Wave, Orange Money, MTN MoMo et Moov Money, traites via notre partenaire securise CinetPay. Aucun paiement par carte requis : votre Mobile Money suffit." },
  { q: "Comment fonctionne le paiement securise ?", a: "Vous cliquez sur Payer, vous etes redirige vers la page de paiement chiffree CinetPay, vous validez sur votre telephone avec votre code secret. Fodindin ne voit et ne stocke jamais votre code Mobile Money." },
  { q: "Y a-t-il des frais ?", a: "Les frais de service « Transaction securisee » sont affiches clairement avant chaque validation. Aucun frais cache : le montant total est indique avant que vous confirmiez." },
  { q: "Que faire en cas de probleme avec une transaction ?", a: "Contactez notre support avec votre reference de transaction (RES-XXXX ou FD-XXXX). En cas de probleme avere (article non conforme, non livre), vous etes rembourse selon nos conditions de protection acheteur." },
  { q: "Comment eviter les arnaques ?", a: "Ne payez JAMAIS en dehors de la plateforme (pas de transfert direct au vendeur avant reception). Privilegiez les vendeurs avec badge « Vendeur verifie » ou badge Or PRO. Signalez tout comportement suspect via le bouton Signaler." },
  { q: "Mes donnees sont-elles protegees ?", a: "Oui. Connexion HTTPS chiffree, verification par SMS (OTP), verrouillage anti-fraude des comptes, et journalisation de securite 24h/24. Vos numeros et codes ne sont jamais partages avec les vendeurs." },
];

export default function FaqPaiementsPage() {
  return (
    <main className="max-w-4xl mx-auto px-6 py-10" data-testid="faq-paiements-page">
      <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full">
        <ShieldCheck size={14} /> Transaction securisee
      </div>
      <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight mt-3">
        FAQ — Paiements securises
      </h1>
      <p className="text-[var(--ink-soft)] mt-2 max-w-2xl">
        Tout ce qu'il faut savoir pour acheter et vendre en toute confiance sur Fodindin, avec vos moyens de paiement Mobile Money habituels.
      </p>

      {/* Moyens de paiement */}
      <section className="mt-10">
        <h2 className="font-bold text-lg inline-flex items-center gap-2 mb-4">
          <Smartphone size={18} className="text-orange-500" /> Les moyens de paiement acceptes
        </h2>
        <div className="space-y-3">
          {METHODS.map((m) => (
            <div key={m.name} className="glass-card p-4 flex items-start gap-3" data-testid={`faq-method-${m.name.toLowerCase().replace(/ /g, "-")}`}>
              <span className="w-10 h-10 rounded-full grid place-items-center text-[10px] font-extrabold shrink-0 shadow-sm" style={{ background: m.bg, color: m.fg }}>
                {m.initials}
              </span>
              <div>
                <div className="font-bold text-sm">{m.name}</div>
                <p className="text-[13px] text-[var(--ink-soft)] mt-0.5">{m.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Garanties */}
      <section className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: RotateCcw, title: "Remboursement", desc: "En cas d'article non conforme ou non livre, selon nos conditions de protection acheteur." },
          { icon: Lock, title: "Paiement chiffre", desc: "HTTPS + agregateur certifie CinetPay. Votre code secret ne quitte jamais votre telephone." },
          { icon: ShieldCheck, title: "Vendeurs verifies", desc: "Badges « Vendeur verifie » (SMS) et « PRO » (abonnes) pour acheter en confiance." },
        ].map((g) => (
          <div key={g.title} className="glass-card p-5 text-center" data-testid={`faq-garantie-${g.title.toLowerCase()}`}>
            <g.icon size={22} className="mx-auto text-emerald-600" />
            <div className="font-bold text-sm mt-2">{g.title}</div>
            <p className="text-xs text-[var(--ink-soft)] mt-1">{g.desc}</p>
          </div>
        ))}
      </section>

      {/* Questions frequentes */}
      <section className="mt-10">
        <h2 className="font-bold text-lg inline-flex items-center gap-2 mb-4">
          <HelpCircle size={18} className="text-orange-500" /> Questions frequentes
        </h2>
        <div className="space-y-2">
          {FAQ.map((f, i) => (
            <details key={i} className="glass-card p-0 overflow-hidden group" data-testid={`faq-item-${i}`}>
              <summary className="flex items-center justify-between gap-3 cursor-pointer select-none p-4 font-semibold text-sm list-none">
                {f.q}
                <ChevronDown size={16} className="shrink-0 text-[var(--ink-soft)] transition-transform group-open:rotate-180" />
              </summary>
              <p className="px-4 pb-4 text-[13px] text-[var(--ink-soft)] leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Contact support */}
      <section className="mt-10 rounded-3xl bg-gradient-to-br from-orange-500 to-orange-400 text-white p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-testid="faq-support-cta">
        <div>
          <h2 className="font-display text-xl font-extrabold">Un probleme avec une transaction ?</h2>
          <p className="text-sm opacity-95 mt-1">Notre equipe repond 7j/7. Gardez votre reference (FD-XXXX / RES-XXXX) a portee de main.</p>
        </div>
        <Link to="/contact" className="btn-secondary inline-flex items-center gap-2 shrink-0" data-testid="faq-contact-btn">
          <MessageCircle size={15} /> Contacter le support
        </Link>
      </section>
    </main>
  );
}
